/**
 * LA CARTERA DE NEXO: QUIEN DEBE QUE MES (PAGOS-NEXO-POR-CLIENTE-T1 punto 6, Josse 6-oct; regla de Ares #23740).
 *
 * DE DONDE SALE. Josse quiere ver, por negocio, los meses que debe y registrar el pago de ese mes desde ahi. La
 * cartera la calcula la API (nexo-api PR 455, 4524243, PAGOS-NEXO-CARTERA-API-T1) y el panel solo la pinta:
 *
 *   GET  /api/v1/nexo-pagos/cartera -> { success, data: nexo_private.nexo_cartera() }
 *        data = { fecha_corte, desde: '2026-08', total_deuda, negocios_en_deuda,
 *                 negocios: [{ business_id, nombre, plan, tarifa, meses_en_deuda: [{ mes_cubierto, monto }],
 *                              total_deuda, cubierta_hasta, ultimo_pago: { fecha_pago, mes_cubierto, monto } | null }],
 *                 pagos_sin_negocio: [{ id, fecha_pago, monto, mes_cubierto, negocio_nombre, candidato_business_id }],
 *                 fuera_de_cartera: { sin_cartera, suscripciones_anuales } }
 *   POST /api/v1/nexo-pagos/{id}/negocio { businessId } -> { success, data: { pago_id, business_id, negocio, mes_cubierto,
 *        negocio_nombre_escrito } } | 400 { error: 'businessId requerido' } | 422 { code: PAGO_*, error }
 *
 * LAS REGLAS (de Ares, las aplica la base; el panel no las recalcula):
 *   - la cartera empieza en 2026-08: lo anterior se cobraba por el link viejo y es historico sin cartera
 *   - un pago SIN negocio no cubre ningun mes hasta que alguien diga de quien es: se lista aparte y se asigna
 *   - "Registrar este mes" registra ESE mes de ESE negocio con la tarifa de la cartera (registrar-y-activar)
 *   - asignar solo sirve para pagos sin negocio: no reasigna
 *
 * Sin DOM: lo prueba scripts/check-cartera-nexo.mjs con la forma EXACTA del 455.
 */

const texto = (v) => (v === null || v === undefined ? '' : String(v).trim());
const numero = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const lista = (v) => (Array.isArray(v) ? v : []);

/** El sobre de la API: res.data = { success, data }. */
const adentro = (res) => {
  const d = res?.data;
  return d && typeof d === 'object' && 'data' in d ? d.data : d;
};

/**
 * La cartera leida de la respuesta, o null si la respuesta no la trae.
 * @returns {null | {fechaCorte: string, desde: string, totalDeuda: number, negociosEnDeuda: number,
 *   negocios: {id: string, nombre: string, plan: string, tarifa: number, meses: {mes: string, monto: number}[],
 *              total: number, cubiertaHasta: string, ultimoPago: null | {fecha: string, mes: string, monto: number}}[],
 *   sinNegocio: {id: string, fecha: string, monto: number, mes: string, nombreEscrito: string, candidato: string}[],
 *   fuera: {sinCartera: number, anuales: number}}}
 */
export function leerCartera(res) {
  const d = adentro(res);
  if (!d || typeof d !== 'object' || !Array.isArray(d.negocios)) return null;
  const negocios = d.negocios.map((n) => {
    const u = n?.ultimo_pago;
    return {
      id: texto(n?.business_id),
      nombre: texto(n?.nombre) || texto(n?.business_id),
      plan: texto(n?.plan),
      tarifa: numero(n?.tarifa),
      meses: lista(n?.meses_en_deuda).map((m) => ({ mes: texto(m?.mes_cubierto), monto: numero(m?.monto) })).filter((m) => m.mes),
      total: numero(n?.total_deuda),
      cubiertaHasta: texto(n?.cubierta_hasta).slice(0, 10),
      ultimoPago: u && typeof u === 'object'
        ? { fecha: texto(u.fecha_pago).slice(0, 10), mes: texto(u.mes_cubierto), monto: numero(u.monto) }
        : null
    };
  }).filter((n) => n.id && n.meses.length);
  return {
    fechaCorte: texto(d.fecha_corte).slice(0, 10),
    desde: texto(d.desde) || '2026-08',
    totalDeuda: numero(d.total_deuda),
    negociosEnDeuda: numero(d.negocios_en_deuda),
    negocios,
    sinNegocio: lista(d.pagos_sin_negocio).map((p) => ({
      id: texto(p?.id),
      fecha: texto(p?.fecha_pago).slice(0, 10),
      monto: numero(p?.monto),
      mes: texto(p?.mes_cubierto),
      nombreEscrito: texto(p?.negocio_nombre),
      candidato: texto(p?.candidato_business_id)
    })).filter((p) => p.id),
    fuera: {
      sinCartera: numero(d.fuera_de_cartera?.sin_cartera),
      anuales: numero(d.fuera_de_cartera?.suscripciones_anuales)
    }
  };
}

/** Que decir cuando la cartera no se pudo leer. 404 = la API servida todavia no tiene la ruta (PR 455). */
export function motivoCartera(res) {
  const code = res?.code;
  if (code === 404 || code === 405) return { sinPublicar: true, mensaje: 'La cartera aun no esta publicada en la API (nexo-api PR 455). Los pagos de abajo siguen sirviendo.' };
  if (code === 403) return { sinPublicar: false, mensaje: 'Tu usuario no tiene permiso para ver la cartera de Nexo.' };
  if (code === 'NETWORK') return { sinPublicar: false, mensaje: 'No pudimos hablar con la API ahora. Revisa la conexion y vuelve a intentar.' };
  return { sinPublicar: false, mensaje: texto(res?.error) || 'No se pudo leer la cartera.' };
}

/**
 * Lo que "Registrar este mes" le pasa al formulario de pago: el negocio, el mes y el monto de la cartera.
 * El formulario sigue mandando (fecha, medio, descuento con motivo): esto solo lo deja lleno.
 */
export function pagoDelMes(negocio, mes) {
  const m = lista(negocio?.meses).find((x) => x.mes === texto(mes));
  if (!negocio?.id || !m) return null;
  return { businessId: negocio.id, nombre: negocio.nombre, mes: m.mes, monto: m.monto || negocio.tarifa };
}

/** El cuerpo de "Asignar": solo el businessId (DTO AsignarNegocioRequest), o el error que lo frena. */
export function cuerpoAsignar(businessId) {
  const id = texto(businessId);
  if (!id) return { ok: false, error: 'Elige de que negocio es este pago' };
  return { ok: true, cuerpo: { businessId: id } };
}

/**
 * El candidato de un pago sin negocio: la API lo propone solo si el nombre escrito casa exacto con UN negocio.
 * Si ese negocio no esta en la lista de activos, no se preselecciona (que Josse elija).
 */
export function candidatoEnLista(pago, negocios) {
  const id = texto(pago?.candidato);
  return id && lista(negocios).some((n) => n.id === id) ? id : '';
}

/** Que decir cuando "Asignar" no pudo, por la causa que manda la base (422 PAGO_*). */
export function motivoAsignar(res) {
  const code = res?.code;
  const det = res?.details || {};
  const causa = texto(det.code);
  if (code === 404 || code === 405) return 'Asignar un pago aun no esta publicado en la API (nexo-api PR 455). No se cambio nada.';
  if (causa === 'PAGO_YA_TIENE_NEGOCIO') return 'Ese pago ya tiene negocio: desde aqui no se reasigna. Recarga la cartera.';
  if (causa === 'PAGO_NO_EXISTE') return 'Ese pago ya no existe. Recarga la cartera.';
  if (causa === 'PAGO_NEGOCIO') return 'Ese negocio no existe en la base. Elige otro de la lista.';
  if (code === 400) return texto(det.error) || 'Elige de que negocio es este pago';
  return texto(det.error) || texto(res?.error) || 'No se pudo asignar el pago';
}

/** "2026-09" -> "sep 2026". */
export function mesParaVer(mes) {
  const m = /^(\d{4})-(\d{2})$/.exec(texto(mes));
  if (!m) return texto(mes);
  const nombres = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  return `${nombres[Number(m[2]) - 1] || m[2]} ${m[1]}`;
}
